import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableSortLabel from '@mui/material/TableSortLabel';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useWorkerErrors } from '../../hooks/useAdmin';
import { formatDate, sortRows, toggleDir } from './adminUtils';

export default function AdminWorkerErrors() {
    const { data: errors = [], isLoading } = useWorkerErrors();
    const [sort, setSort] = useState({ key: 'timestamp', dir: 'desc' });
    const [expanded, setExpanded] = useState({});
    const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

    const sorted = sortRows(errors, sort.key, sort.dir, (r, k) => {
        if (k === 'timestamp') return r.timestamp;
        if (k === 'job') return r.params?.job ?? '';
        return '';
    });

    const sp = (key) => ({
        active: sort.key === key,
        direction: sort.key === key ? sort.dir : 'asc',
        onClick: () => setSort(prev => toggleDir(prev, key)),
    });

    if (isLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2, py: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50', mb: 2 }}>
                Greške workera (zadnjih 20)
            </Typography>
            <Paper sx={{ overflow: 'auto' }}>
                {errors.length === 0 ? (
                    <Box sx={{ p: 2 }}>
                        <Typography color="text.secondary" sx={{ color: '#27ae60' }}>Nema evidentiranih grešaka.</Typography>
                    </Box>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 600 }}><TableSortLabel {...sp('timestamp')}>Vrijeme</TableSortLabel></TableCell>
                                <TableCell sx={{ fontWeight: 600 }}><TableSortLabel {...sp('job')}>Posao</TableSortLabel></TableCell>
                                <TableCell sx={{ width: 40 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sorted.map((err) => (
                                <React.Fragment key={err.id}>
                                    <TableRow hover onClick={() => toggle(err.id)} sx={{ cursor: 'pointer' }}>
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 13, color: '#e74c3c', fontWeight: 600 }}>
                                            {formatDate(err.timestamp)}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13, color: '#555' }}>
                                            {err.params?.job ?? '?'}
                                            {err.params?.search_name ? ` — "${err.params.search_name}"` : ''}
                                        </TableCell>
                                        <TableCell align="right" sx={{ p: 0.5 }}>
                                            <IconButton size="small">
                                                <ExpandMoreIcon fontSize="small" sx={{ transform: expanded[err.id] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={3} sx={{ py: 0, border: expanded[err.id] ? undefined : 0 }}>
                                            <Collapse in={!!expanded[err.id]} unmountOnExit>
                                                <Box component="pre" sx={{ fontSize: 12, backgroundColor: '#1e1e1e', color: '#f8f8f2', p: 2, borderRadius: 1, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', m: 1 }}>
                                                    {err.params?.traceback ?? JSON.stringify(err.params, null, 2)}
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Paper>
        </Box>
    );
}
